import { useQuery } from '@tanstack/react-query';
import { alertsApi } from '../../api/dashboard';
import { timeOfDay } from '../../lib/format';

interface Props {
  onAlertClick: (code: string) => void;
}

export function AlertsPanel({ onAlertClick }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['alerts', 'open', 6],
    queryFn: () => alertsApi.list('open', 6),
    refetchInterval: 30_000,
  });

  return (
    <div className="bg-white border border-cream-200 rounded-sm flex flex-col">
      <div className="px-5 py-4 border-b border-cream-100 flex items-center justify-between">
        <div>
          <h3 className="serif-display text-base text-navy-800 font-medium accent-underline inline-block">Live Alerts</h3>
          <p className="text-xs text-navy-800/60 mt-3">Click to view, acknowledge, escalate</p>
        </div>
        {data && data.length > 0 && (
          <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium font-numeric">
            {data.filter((a) => a.severity === 'critical').length} CRITICAL
          </span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto" style={{ maxHeight: 480 }}>
        {isLoading || !data ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="px-5 py-3 border-b border-cream-100">
              <div className="h-3 w-3/4 bg-cream-200 rounded animate-pulse" />
              <div className="h-2 w-1/3 bg-cream-200 rounded animate-pulse mt-2" />
            </div>
          ))
        ) : (
          data.map((a) => (
            <button
              type="button"
              key={a.id}
              onClick={() => onAlertClick(a.id)}
              className="w-full text-left px-5 py-3 border-b border-cream-100 hover:bg-cream-50 transition"
            >
              <div className="flex items-start gap-2.5">
                <span
                  className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                    a.severity === 'critical' ? 'bg-red-600' :
                    a.severity === 'warning'  ? 'bg-amber-500' :
                                                'bg-blue-400'
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-navy-800">
                    {a.district ? `${a.district} · ` : ''}
                    {a.title.length > 52 ? a.title.slice(0, 52) + '…' : a.title}
                  </div>
                  <div className="text-[10px] text-navy-800/50 mt-1 font-numeric">
                    {timeOfDay(a.triggered_at)} · {a.owner}
                  </div>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
